import React, { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import Button from '../ui/Button';
import HelpTooltip from '../ui/HelpTooltip';
import AssignmentDrawer, { mergeRosterPeople } from '../AssignmentDrawer';
import type {
  AssignmentGroupRef,
  AssignmentPerson,
  AssignmentValue,
} from '../AssignmentDrawer';
import type { SelectId } from '../MultiSelect';
import { compareNames } from '../../utils/text';
import './AssignmentField.css';

/** Names listed inline before the summary switches to "+N more". */
const NAME_PREVIEW = 4;

interface AssignmentFieldProps {
  label: string;
  labelHelpText?: string;
  people: AssignmentPerson[];
  /** Omit for a people-only field — no group tags, no provenance breakdown. */
  groups?: AssignmentGroupRef[];
  memberIds: SelectId[];
  groupIds: SelectId[];
  onChange: (next: AssignmentValue) => void;
  /** Dialog title; defaults to the field label. */
  drawerTitle?: string;
  id?: string;
  /** Labels already saved on the record (see AssignmentDrawer). */
  savedGroupIds?: SelectId[];
  loadGroupMembers?: (groupId: SelectId) => Promise<SelectId[]>;
  /** Saved roster by name (record GET); fills in people outside the scoped list. */
  rosterPeople?: AssignmentPerson[];
}

const key = (id: SelectId) => String(id);

/**
 * Fixed-height replacement for the members + groups chip fields.
 *
 * The old control rendered one chip per assignment, so form height was a
 * function of roster size and the field could dwarf everything it sat beside.
 * This stays the same height at any count and moves editing into
 * {@link AssignmentDrawer}, which is also where groups now live — so the roster
 * and the thing that produces it are managed together.
 */
const AssignmentField: React.FC<AssignmentFieldProps> = ({
  label,
  labelHelpText,
  people,
  groups,
  memberIds,
  groupIds,
  onChange,
  drawerTitle,
  id,
  savedGroupIds,
  loadGroupMembers,
  rosterPeople,
}) => {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);

  const groupsEnabled = Array.isArray(groups) && groups.length > 0;
  const groupIdSet = useMemo(() => new Set(groupIds.map(key)), [groupIds]);
  const memberIdSet = useMemo(() => new Set(memberIds.map(key)), [memberIds]);

  const allPeople = useMemo(() => mergeRosterPeople(people, rosterPeople), [people, rosterPeople]);

  // Groups are labels: the roster is memberIds alone.
  const assigned = useMemo(
    () =>
      allPeople
        .filter((p) => memberIdSet.has(key(p._id)))
        .sort((a, b) => compareNames(a.name, b.name)),
    [allPeople, memberIdSet],
  );
  // Full roster size, even for ids no list can name.
  const rosterCount = memberIdSet.size;

  const inLabel = (p: AssignmentPerson) =>
    (p.groups ?? []).some((g) => groupIdSet.has(key(g._id)));
  const viaGroupCount = assigned.filter(inLabel).length;
  const labelPresent = (g: AssignmentGroupRef) =>
    assigned.filter((p) => (p.groups ?? []).some((pg) => key(pg._id) === key(g._id))).length;
  const labelSize = (g: AssignmentGroupRef) =>
    g.memberCount ??
    allPeople.filter((p) => (p.groups ?? []).some((pg) => key(pg._id) === key(g._id))).length;

  const assignedGroups = useMemo(
    () =>
      (groups ?? [])
        .filter((g) => groupIdSet.has(key(g._id)))
        .sort((a, b) => compareNames(a.name, b.name)),
    [groups, groupIdSet],
  );

  const preview = assigned.slice(0, NAME_PREVIEW).map((p) => p.name).join(', ');
  const overflow = rosterCount - Math.min(assigned.length, NAME_PREVIEW);

  return (
    <div className="asgf" id={id} data-testid="assignment-field">
      <span className="asgf__label" data-testid="assignment-field-label">
        <HelpTooltip helpText={labelHelpText ?? (groupsEnabled ? t('assign.groupsAreCopied') : undefined)}>
          {rosterCount > 0 ? `${label} (${rosterCount})` : label}
        </HelpTooltip>
      </span>

      <div className="asgf__box">
        {assignedGroups.length > 0 && (
          <div className="asgf__sources" data-testid="assignment-field-sources">
            {assignedGroups.map((g) => (
              <span className="asgf__tag" data-testid="assignment-field-tag" key={key(g._id)}>
                {t('assign.labelTag', { group: g.name, present: labelPresent(g), size: labelSize(g) })}
              </span>
            ))}
          </div>
        )}

        <p className="asgf__names" data-testid="assignment-field-names">
          {rosterCount === 0 ? (
            <span className="asgf__empty">{t('assign.noneAssigned')}</span>
          ) : (
            <>
              {preview}
              {overflow > 0 && (
                <span className="asgf__more"> {t('assign.moreCount', { count: overflow })}</span>
              )}
            </>
          )}
        </p>

        <div className="asgf__foot">
          <Button variant="secondary" size="sm" data-testid="assignment-manage" onClick={() => setOpen(true)}>
            {t('assign.manage', { count: rosterCount })}
          </Button>
          {groupsEnabled && (
            <span className="asgf__meta" data-testid="assignment-field-meta">
              {t('assign.breakdown', {
                groups: assignedGroups.length,
                viaGroups: viaGroupCount,
                direct: rosterCount - viaGroupCount,
              })}
            </span>
          )}
        </div>
      </div>

      <AssignmentDrawer
        isOpen={open}
        onClose={() => setOpen(false)}
        title={drawerTitle ?? label}
        people={allPeople}
        groups={groups}
        value={{ memberIds, groupIds }}
        onApply={onChange}
        savedGroupIds={savedGroupIds}
        loadGroupMembers={loadGroupMembers}
      />
    </div>
  );
};

export default AssignmentField;
